import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";
import logo from "../Images/buillogo6.png";
import anotherLogo from "../Images/OGGS_Logo.png";

/**
 * Generate Proforma (before payment) or Invoice (after payment)
 * UI-only improvements (alignment, wrapping, dynamic heights, strict page-fit).
 * 💡 AMOUNTS / LOGIC UNCHANGED.
 *
 * @param {Object} sellerDetails - seller object (name, address, email, gstNumber, etc.)
 * @param {Object} options
 * @param {boolean} options.isPaid - true => final invoice, false => proforma
 * @param {Object} [options.paymentInfo] - { orderId, paymentId, method }
 */
const ReggenerateInvoice = (sellerDetails = {}, options = {}) => {
  const { isPaid = false, paymentInfo = {} } = options;

  // ---- Doc Setup ----
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const page = {
    w: doc.internal.pageSize.getWidth(),
    h: doc.internal.pageSize.getHeight(),
  };
  const M = 12; // page margin
  const brand = { r: 24, g: 66, b: 110 }; // deep blue
  const accent = { r: 41, g: 128, b: 185 }; // light blue
  const light = { r: 245, g: 247, b: 250 }; // soft bg
  const lineHeightFactor = 1.25;

  const now = dayjs();
  const formattedDate = now.format("DD MMMM YYYY");
  const invoiceId = now.format("YYYYMMDDHHmmss");

  // ---- Amounts (UNCHANGED LOGIC) ----
  const gstRate = 0.18;
  const originalBase = 10000;

  const isOffline = sellerDetails.discountApplied === true;
  const discountPercentage = sellerDetails.couponPercentage || 0;

  let discountAmount = 0;
  let discountedBase = originalBase;
  let gstAmount = 0;
  let grandTotal = 0;

  if (isOffline && discountPercentage > 0) {
    discountAmount = (originalBase * discountPercentage) / 100;
    discountedBase = originalBase - discountAmount;
  } else {
    discountedBase = originalBase;
  }

  gstAmount = discountedBase * gstRate;
  grandTotal = discountedBase + gstAmount;

  // ---- Helpers ----
  const safe = (v) => (v == null || v === "" ? "-" : String(v));
  const splitToWidth = (t, maxW) => doc.splitTextToSize(safe(t), maxW);
  const ensureSpace = (y, needed) => {
    if (y + needed > page.h - M) {
      doc.addPage();
      return M;
    }
    return y;
  };
  const measureLinesH = (lines) => {
    const text = Array.isArray(lines) ? lines.join("\n") : String(lines || "-");
    const dims = doc.getTextDimensions(text);
    // jsPDF returns dimensions for current font size; add a small buffer to avoid clipping
    return dims.h + 1.5;
  };
  const sectionTitle = (text, y) => {
    doc.setFillColor(light.r, light.g, light.b);
    doc.roundedRect(M, y - 6, page.w - 2 * M, 10, 2, 2, "F");
    doc.setTextColor(brand.r, brand.g, brand.b);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(text, M + 3, y + 1);
    doc.setTextColor(0, 0, 0);
  };

  // ---- Header Banner ----
  const headerH = 24;
  doc.setFillColor(brand.r, brand.g, brand.b);
  doc.rect(0, 0, page.w, headerH + 6, "F");

  // Logos (robust, don't break rendering if missing)
  try {
    if (anotherLogo) doc.addImage(anotherLogo, "PNG", M, 6, 34, 14);
  } catch {}
  try {
    if (logo) doc.addImage(logo, "PNG", page.w - M - 34, 6, 34, 14);
  } catch {}

  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(isPaid ? "INVOICE" : "PROFORMA INVOICE", page.w / 2, 15, { align: "center" });
  doc.setTextColor(0, 0, 0);

  let y = headerH + 10;

  // ---- Invoice Meta Box ----
  doc.setDrawColor(accent.r, accent.g, accent.b);
  doc.setLineWidth(0.4);
  doc.roundedRect(M, y, page.w - 2 * M, 20, 2, 2);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const addMetaRow = (label, value, x, yy) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, x, yy);
    doc.setFont("helvetica", "normal");
    doc.text(safe(value), x + 26, yy);
  };

  addMetaRow("Date:", formattedDate, M + 4, y + 7);
  addMetaRow("Invoice ID:", `#${invoiceId}`, M + 4, y + 13);
  addMetaRow("Payment:", isPaid ? "PAID" : "UNPAID", M + 4, y + 19);

  if (isPaid) {
    const rx = page.w / 2 + 5;
    addMetaRow("Order ID:", paymentInfo.orderId, rx, y + 7);
    addMetaRow("Payment ID:", paymentInfo.paymentId, rx, y + 13);
    addMetaRow("Method:", paymentInfo.method, rx, y + 19);
  }

  y += 28;

  // ---- Company & Seller Info ----
  sectionTitle("Billing Information", y);
  y += 10;

  const colGap = 6;
  const colW = (page.w - 2 * M - colGap) / 2;
  const leftX = M;
  const rightX = M + colW + colGap;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  // Padded content area inside each box
  const BOX_PAD = 6;

  // Company (wrapped to strict width)
  const companyWrapped = [
    ...splitToWidth("OGCS Private Limited, BuildPro OGCS", colW - 2 * BOX_PAD),
    ...splitToWidth("Navin Panvel, Dist: Raigad, Pin Code: 410206", colW - 2 * BOX_PAD),
    ...splitToWidth("Contact: +91 9967610135", colW - 2 * BOX_PAD),
    ...splitToWidth("Email: support@buildproogcs.com", colW - 2 * BOX_PAD),
    ...splitToWidth("GST: 27AAEC01131F1ZW", colW - 2 * BOX_PAD),
  ];

  // Seller (wrapped to strict width)
  const sellerWrapped = [
    ...splitToWidth(safe(sellerDetails.name), colW - 2 * BOX_PAD),
    ...splitToWidth(
      [safe(sellerDetails.address?.street), safe(sellerDetails.address?.city)].filter(Boolean).join(", "),
      colW - 2 * BOX_PAD
    ),
    ...splitToWidth(
      [
        safe(sellerDetails.address?.state),
        safe(sellerDetails.address?.country),
        sellerDetails.address?.postalCode ? `- ${sellerDetails.address?.postalCode}` : "",
      ]
        .filter(Boolean)
        .join(", "),
      colW - 2 * BOX_PAD
    ),
    ...splitToWidth(`Contact: ${safe(sellerDetails.mobile)}`, colW - 2 * BOX_PAD),
    ...splitToWidth(`Email: ${safe(sellerDetails.email)}`, colW - 2 * BOX_PAD),
    ...splitToWidth(`PAN: ${safe(sellerDetails.pan)}`, colW - 2 * BOX_PAD),
    ...splitToWidth(`GST: ${safe(sellerDetails.gstNumber)}`, colW - 2 * BOX_PAD),
  ].filter((l) => String(l).trim() !== "");

  // Dynamic heights based on text measurements (plus titles/padding)
  const companyH = measureLinesH(companyWrapped);
  const sellerH = measureLinesH(sellerWrapped);
  const titleH = 7; // "From:" label area
  const contentPad = 6; // top content spacing below label
  const boxH = Math.max(32, titleH + contentPad + Math.max(companyH, sellerH) + BOX_PAD);

  y = ensureSpace(y, boxH);

  // Draw boxes
  doc.roundedRect(leftX, y, colW, boxH, 2, 2, "S");
  doc.roundedRect(rightX, y, colW, boxH, 2, 2, "S");

  // Titles
  doc.setFont("helvetica", "bold");
  doc.text("From:", leftX + BOX_PAD, y + 7);
  doc.text("Bill To:", rightX + BOX_PAD, y + 7);

  // Contents
  doc.setFont("helvetica", "normal");
  const contentY = y + 7 + contentPad;
  doc.text(companyWrapped, leftX + BOX_PAD, contentY, {
    maxWidth: colW - 2 * BOX_PAD,
    lineHeightFactor,
  });
  doc.text(sellerWrapped, rightX + BOX_PAD, contentY, {
    maxWidth: colW - 2 * BOX_PAD,
    lineHeightFactor,
  });

  y += boxH + 10;

  // ---- Charges Table ----
  sectionTitle("Charges", y);
  y += 6;

  const tableHeaders = ["Description", "Amount (₹)"];
  const tableData = [];

  if (isOffline && discountAmount > 0) {
    tableData.push(["Original Registration Fee", `${originalBase.toFixed(2)}`]);
    tableData.push([`Coupon Discount (${discountPercentage}%)`, `-${discountAmount.toFixed(2)}`]);
    tableData.push(["Discounted Registration Fee", `${discountedBase.toFixed(2)}`]);
  } else {
    tableData.push(["Seller Registration Fee", `${discountedBase.toFixed(2)}`]);
  }

  tableData.push(["GST (18%)", `${gstAmount.toFixed(2)}`]);
  tableData.push(["Total Amount", `${grandTotal.toFixed(2)}`]);

  doc.autoTable({
    head: [tableHeaders],
    body: tableData,
    startY: y + 2,
    theme: "striped",
    styles: { fontSize: 10, cellPadding: 3 },
    headStyles: {
      fillColor: [brand.r, brand.g, brand.b],
      textColor: [255, 255, 255],
      halign: "center",
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 0: { halign: "left" }, 1: { halign: "right" } },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === tableData.length - 1) {
        data.cell.styles.fontStyle = "bold";
      }
    },
    margin: { left: M, right: M },
  });

  let afterTableY = doc.lastAutoTable.finalY;

  // ---- Payment Details (two strict columns inside the box) ----
  // Compute inner layout so columns NEVER exceed the box width.
  const BOX_INNER_PAD = 6; // left & right padding inside bank box
  const COL_GAP = 6; // gap between left/right columns inside the bank box
  const bankBoxInnerTotal = page.w - 2 * M - 2 * BOX_INNER_PAD - COL_GAP; // inner usable width
  const bankColW = bankBoxInnerTotal / 2;

  const bankLeft = [
    "Bank Name: HDFC Bank",
    "Account Name: OGCS Private Limited",
    "Account Number: 50200084105798",
  ];
  const bankRight = ["IFSC Code: HDFC0000256", "Branch: New Panvel"];

  const bankLeftWrapped = bankLeft.flatMap((l) => splitToWidth(l, bankColW));
  const bankRightWrapped = bankRight.flatMap((l) => splitToWidth(l, bankColW));

  const bankContentH = Math.max(measureLinesH(bankLeftWrapped), measureLinesH(bankRightWrapped));
  const BANK_VERT_PAD = 8; // top/bottom pad inside box
  const bankBoxH = Math.max(22, bankContentH + BANK_VERT_PAD);

  // Ensure space for bank box + thank-you section
  let bankY = ensureSpace(afterTableY + 8, bankBoxH + 24);

  sectionTitle("Payment Details", bankY);
  bankY += 6;

  // Draw bank box
  doc.setDrawColor(220);
  doc.roundedRect(M, bankY, page.w - 2 * M, bankBoxH, 2, 2, "S");

  // Column start positions INSIDE the box
  const bankLeftX = M + BOX_INNER_PAD;
  const bankRightX = bankLeftX + bankColW + COL_GAP;
  const bankTextY = bankY + BANK_VERT_PAD / 2 + 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(bankLeftWrapped, bankLeftX, bankTextY, {
    maxWidth: bankColW,
    lineHeightFactor,
  });
  doc.text(bankRightWrapped, bankRightX, bankTextY, {
    maxWidth: bankColW,
    lineHeightFactor,
  });

  // ---- Thank You block ----
  let afterBankY = bankY + bankBoxH + 8;
  afterBankY = ensureSpace(afterBankY, 14);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(brand.r, brand.g, brand.b);
  doc.text("Thank you for connecting with BuildPro OGCS!", page.w / 2, afterBankY, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text("For assistance, contact our support team.", page.w / 2, afterBankY + 6, { align: "center" });

  // ---- Per-page border + page numbers ----
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(230);
    doc.setLineWidth(0.3);
    doc.rect(5, 5, page.w - 10, page.h - 10);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Page ${i} of ${pageCount}`, page.w / 2, page.h - 6, { align: "center" });
  }

  // ---- Save PDF ----
  const safeName = (sellerDetails.name || "Seller").replace(/\s+/g, "_");
  const filePrefix = isPaid ? "Invoice" : "Proforma_Invoice";
  doc.save(`${filePrefix}_${safeName}_${now.format("YYYYMMDD")}.pdf`);
};

export default ReggenerateInvoice;
